import { Head } from "@inertiajs/react"
import { type TFunction } from "i18next"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  type BadgeDetailData,
  BadgeDetailSheet,
} from "@/components/badges/badge-detail-sheet"
import { BadgeSectionCard } from "@/components/badges/badge-section-card"
import { BadgeTile } from "@/components/badges/badge-tile"
import AppLayout from "@/layouts/app-layout"
import { isNewlyEarned, tierFor } from "@/lib/badge-tiers"
import type { CreatorBadgesProps, EarnedTile, LockedTile } from "@/types/badges"

/**
 * OKS-50 / OKS-527 — Creator §14 badges goal board (`/profile/badges`).
 *
 * Two grouped section cards (EARNED · N / TO UNLOCK · M) replacing the
 * old All/Earned/Locked tabs, a header summary line ("N earned · M to
 * unlock · next: {badge}"), and a tap-to-open detail sheet. Every tile
 * carries a crew-internal holder count + a scarcity rarity pill.
 *
 * # Brand isolation
 *
 * `earned` / `locked` / `summary` are scoped server-side to
 * `Current.membership` + its brand. Holder counts are brand-internal
 * aggregates (count only). No client-side filter widens the surface;
 * switching brand triggers a full Inertia visit via `CrewSwitcher`.
 *
 * # Detail sheet (TRD KD-2)
 *
 * The sheet reads a resolved view model built HERE from the loaded props
 * — no second fetch, no new endpoint. Selecting a tile builds its
 * `BadgeDetailData`; closing clears it.
 */
export default function CreatorBadges({
  earned,
  locked,
  summary,
}: CreatorBadgesProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const [selected, setSelected] = useState<BadgeDetailData | null>(null)

  const nextName = useMemo(() => {
    if (!summary.next_identifier) return null
    const tile = locked.find((l) => l.identifier === summary.next_identifier)
    return tile ? lockedName(tile, t) : null
  }, [summary.next_identifier, locked, t])

  const summaryLine = nextName
    ? t("badges.creator.summary_with_next", {
        earned: summary.earned_count,
        locked: summary.locked_count,
        next: nextName,
      })
    : t("badges.creator.summary", {
        earned: summary.earned_count,
        locked: summary.locked_count,
      })

  return (
    <AppLayout>
      <Head title={t("badges.creator.title")} />

      <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6">
        <header className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold">{t("badges.creator.title")}</h1>
          <p
            className="text-muted-foreground text-sm"
            data-testid="badges-summary"
          >
            {summaryLine}
          </p>
        </header>

        <BadgeSectionCard
          title={t("badges.creator.section.earned")}
          count={summary.earned_count}
          emptyMessage={t("badges.creator.empty_earned")}
        >
          {earned.map((tile) => (
            <EarnedTileView
              key={tile.identifier}
              tile={tile}
              locale={locale}
              onOpen={() => setSelected(buildEarnedDetail(tile, locale, t))}
            />
          ))}
        </BadgeSectionCard>

        {locked.length > 0 && (
          <BadgeSectionCard
            title={t("badges.creator.section.to_unlock")}
            count={summary.locked_count}
          >
            {locked.map((tile) => (
              <LockedTileView
                key={tile.identifier}
                tile={tile}
                onOpen={() => setSelected(buildLockedDetail(tile, t, locale))}
              />
            ))}
          </BadgeSectionCard>
        )}

        <BadgeDetailSheet badge={selected} onClose={() => setSelected(null)} />
      </main>
    </AppLayout>
  )
}

// ---- Name / label resolution ----

function milestoneName(
  i18nKey: string,
  month: EarnedTile["month"],
  mission: EarnedTile["mission"],
  locale: string,
  t: TFunction,
): string {
  if (i18nKey === "top_performer" && month) {
    const monthName = new Date(month.year, month.month - 1, 1).toLocaleString(
      locale,
      { month: "long", year: "numeric" },
    )
    return t("badges.milestones.top_performer.name_with_month", {
      month: monthName,
    })
  }
  // OKS-966 — a creator can hold several `first_publisher` rows, so each
  // earned tile names its mission; without this they render byte-identical.
  // The test is PRESENCE, not nil (UX Spec §4.3): a missing, null, or
  // blank-titled mission falls back to the bare family name rather than
  // rendering a dangling separator.
  if (i18nKey === "first_publisher" && mission?.title?.trim()) {
    return t("badges.milestones.first_publisher.name_with_mission", {
      mission: mission.title,
    })
  }
  return t(`badges.milestones.${i18nKey}.name`)
}

function earnedName(tile: EarnedTile, locale: string, t: TFunction): string {
  if (tile.kind === "milestone" && tile.i18n_key) {
    return milestoneName(tile.i18n_key, tile.month, tile.mission, locale, t)
  }
  return tile.name ?? ""
}

function lockedName(tile: LockedTile, t: TFunction): string {
  return t(`badges.milestones.${tile.i18n_key}.name`)
}

function categoryLabel(category: string, t: TFunction): string {
  return t(`badges.category.${category}`)
}

// ---- Detail view-model builders (TRD KD-2 — from loaded props) ----

function buildEarnedDetail(
  tile: EarnedTile,
  locale: string,
  t: TFunction,
): BadgeDetailData {
  const name = earnedName(tile, locale, t)
  const howToEarn =
    tile.kind === "milestone" && tile.i18n_key
      ? t(`badges.milestones.${tile.i18n_key}.description`)
      : (tile.description ?? "")
  const earnedDate = new Date(tile.earned_at).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  return {
    kind: tile.kind,
    state: "earned",
    name,
    icon: tile.icon,
    accentColor: tile.accent_color,
    // OKS-573 — thread the resolved image for BOTH kinds: custom badges
    // and milestones with a brand override both render frame-less.
    imageUrl: tile.image_url ?? undefined,
    tier: tierFor({
      kind: tile.kind,
      identifier: tile.identifier,
      accentColor: tile.accent_color,
    }),
    categoryLabel: categoryLabel(tile.category, t),
    rarity: tile.rarity,
    howToEarn,
    holderCount: tile.holder_count,
    earnedAtLabel: t("badges.creator.earned_at", { date: earnedDate }),
  }
}

function buildLockedDetail(
  tile: LockedTile,
  t: TFunction,
  locale: string,
): BadgeDetailData {
  const remaining = tile.progress
    ? tile.progress.threshold - tile.progress.current
    : (tile.threshold ?? 0)

  // OKS-558 — thousands separators so large view thresholds read
  // "94,000 to go" / "…more views" rather than "94000". Locale-aware.
  const remainingLabel = remaining.toLocaleString(locale)

  // OKS-967 (AC-7) — a locked tile whose remaining has reached 0 renders the
  // generic "target reached" hint instead of an "Encore 0" countdown, and drops
  // the to-go label entirely.
  //
  // GENERIC BY DESIGN — no per-family branch. Every shipped family self-heals out
  // of this state within minutes, because its numerator is re-evaluated by the
  // same job or transaction that moves it. `ultra_reactive` re-counts only when
  // the creator next accepts a mission, which may be weeks, so this turns a state
  // nobody sat in into one a creator can hold indefinitely.
  //
  // ⚠️ `<= 0`, NOT `=== 0`. The server already clamps `current` to `threshold`
  // (`CreatorBadgesController#locked_progress`), so an equality test is inert
  // against the mutation that matters and reads as if it were guarding something.
  //
  // OKS-964 (AC-7) — ⚠️ THE `tile.progress != null` CONJUNCT IS A BUG FIX, NOT A
  // TIGHTENING. Do not simplify it away.
  //
  // Readiness is only a meaningful question for a tile that CARRIES a bar. For a
  // threshold-less family the wire sends `threshold: null` AND `progress: null`
  // (`CreatorBadgesController#threshold_for` returns `meta[:threshold]`;
  // `#locked_progress` returns nil when it is nil), so `remaining` fell through
  // to `tile.threshold ?? 0` = 0 and `ready` was unconditionally TRUE. The sheet
  // then rendered the generic `locked_hint_ready` — "Objectif atteint — badge en
  // route." — for a badge that is not on its way and has no objective to reach.
  //
  // That was LIVE for `top_performer` and `first_publisher`: their `locked_hint`
  // strings were dead on this surface and the copy shown was FALSE. It also
  // caught misconfigured `brand_expert` (no ranks ⇒ `locked_progress` nil while
  // `threshold_for` returns 0). One predicate repairs all three and makes
  // `coup_de_coeur`'s hint reachable at all.
  //
  // ⚠️ THE SHIPPED OKS-967 AC-7 SUITE CANNOT SEE THIS. Every one of its examples
  // drives a family that HAS a progress block (`openLocked({current, threshold})`),
  // so `tile.progress` is non-null and the conjunct reduces to the original
  // `remaining <= 0` — all of them pass unchanged, with the fix and without it.
  // The only guard is an example built on a threshold-less locked tile.
  const ready = tile.progress != null && remaining <= 0

  return {
    kind: "milestone",
    state: "locked",
    name: lockedName(tile, t),
    icon: tile.icon,
    accentColor: tile.accent_color,
    // OKS-573 — a locked milestone can still carry a brand override.
    imageUrl: tile.image_url ?? undefined,
    tier: tierFor({ kind: "milestone", identifier: tile.identifier }),
    categoryLabel: categoryLabel(tile.category, t),
    rarity: tile.rarity,
    howToEarn: ready
      ? t("badges.creator.locked_hint_ready")
      : t(`badges.creator.locked_hint.${tile.i18n_key}`, {
          remaining: remainingLabel,
        }),
    holderCount: tile.holder_count,
    progress: tile.progress,
    toGoLabel:
      tile.progress && !ready
        ? t("badges.detail.x_to_go", { count: remainingLabel })
        : null,
  }
}

// ---- Tiles ----

function EarnedTileView({
  tile,
  locale,
  onOpen,
}: {
  tile: EarnedTile
  locale: string
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const name = earnedName(tile, locale, t)
  const description =
    tile.kind === "milestone" && tile.i18n_key
      ? t(`badges.milestones.${tile.i18n_key}.description`)
      : tile.description

  const earnedDate = new Date(tile.earned_at).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  const tier = tierFor({
    kind: tile.kind,
    identifier: tile.identifier,
    accentColor: tile.accent_color,
  })

  const isCustom = tile.kind === "custom"

  return (
    <BadgeTile
      variant={isCustom ? "image" : "medallion"}
      imageUrl={tile.image_url}
      icon={tile.icon}
      accentColor={tile.accent_color}
      name={name}
      tier={tier}
      isNew={isNewlyEarned(tile.earned_at)}
      newLabel={t("badges.new")}
      hint={t("badges.creator.earned_at", { date: earnedDate })}
      // OKS-937 — UNCHANGED by design (PRD AC5: catalogue announcements stay
      // byte-identical). A description-less branch was drafted here and then
      // removed: the premise was measured false. `badges.description` is
      // `null: false` with a `char_length >= 1` CHECK and a model presence
      // validation, and every milestone family carries an i18n key — so the
      // `?? ""` below is defensive padding with no input that can reach it,
      // not evidence that the blank case occurs. The preview is different:
      // there the description is STRUCTURALLY absent from `RecentBadgeTile`,
      // which is why `tile_earned_brief` exists and is used only there.
      ariaLabel={t("badges.aria.tile_earned", {
        name,
        tier: t(`badges.tier.${tier}`),
        description: description ?? "",
      })}
      earnedAt={tile.earned_at}
      holderCount={tile.holder_count}
      rarity={tile.rarity}
      onClick={onOpen}
    />
  )
}

function LockedTileView({
  tile,
  onOpen,
}: {
  tile: LockedTile
  onOpen: () => void
}) {
  const { t } = useTranslation()

  const name = lockedName(tile, t)
  const category = categoryLabel(tile.category, t)
  const progressCaption = tile.progress
    ? `${tile.progress.current}/${tile.progress.threshold} · ${category}`
    : undefined

  const tier = tierFor({
    kind: "milestone",
    identifier: tile.identifier,
    accentColor: tile.accent_color,
  })

  return (
    <BadgeTile
      imageUrl={tile.image_url}
      icon={tile.icon}
      accentColor={tile.accent_color}
      name={name}
      tier={tier}
      ariaLabel={t("badges.aria.tile_locked", {
        name,
        tier: t(`badges.tier.${tier}`),
      })}
      progress={tile.progress ?? undefined}
      progressCaption={progressCaption}
      holderCount={tile.holder_count}
      rarity={tile.rarity}
      onClick={onOpen}
    />
  )
}
